// Excel-style format strings → display strings. FREE-TEXT input; parser is
// best-effort and degrades gracefully when the format string is unrecognised
// (returns String(value), per Nitzan's spec).
//
// Supported number patterns:
//   0, 0.0, 0.00, 0.000 ...                   — required-digit decimals
//   #,##0, #,##0.00 ...                       — thousands grouping
//   0%, 0.00%, #,##0.00% ...                  — percentage (multiplies by 100)
//   leading/trailing literal text in quotes:  "$"#,##0.00
//   plain trailing literal chars (e.g. ' ש"ח) are accepted
//
// Supported date tokens (replaced left-to-right after first matching the
// LONGEST tokens to avoid ambiguity):
//   yyyy  yy        — year
//   MM    M         — month (UPPERCASE only; lowercase mm = minutes)
//   dd    d         — day
//   HH    H         — 24h hour
//   hh    h         — 12h hour
//   mm    m         — minutes (lowercase)
//   ss    s         — seconds
// Everything between tokens passes through (/, -, ., ' ', ':', etc.).

// ---------- Padding helpers ----------

function pad2(n: number): string { return n < 10 ? '0' + n : String(n); }
function pad4(n: number): string {
  if (n >= 1000) return String(n);
  if (n >= 100)  return '0' + n;
  if (n >= 10)   return '00' + n;
  return '000' + n;
}

// ---------- Number parser ----------

interface ParsedNumberFormat {
  multiplier: number;
  prefix: string;
  suffix: string;
  useGrouping: boolean;
  minDecimals: number;
  maxDecimals: number;
}

function stripLiterals(format: string): { prefix: string; core: string; suffix: string } {
  // Pull out double-quoted literal segments, plus any non-digit-format
  // characters at the very start/end (like leading `$` or trailing ` ש"ח`).
  // We do this in a lenient way — just enough to handle the common cases.
  let core = format;
  let prefix = '';
  let suffix = '';

  // Quoted segments at the start.
  const startQuote = core.match(/^"([^"]*)"/);
  if (startQuote) {
    prefix = startQuote[1];
    core = core.slice(startQuote[0].length);
  }
  const endQuote = core.match(/"([^"]*)"$/);
  if (endQuote) {
    suffix = endQuote[1];
    core = core.slice(0, -endQuote[0].length);
  }
  return { prefix, core, suffix };
}

function parseNumberFormat(format: string): ParsedNumberFormat | null {
  // First section only (positive). Excel uses ';' for separate sections —
  // out of scope for v1.
  let work = format.split(';')[0].trim();
  if (work.length === 0) return null;

  let multiplier = 1;
  let extraSuffix = '';

  // Trailing '%' → percentage (Excel semantics: multiplies by 100).
  if (work.endsWith('%')) {
    multiplier = 100;
    extraSuffix = '%';
    work = work.slice(0, -1);
  }

  // Strip prefix/suffix literals (quoted).
  const stripped = stripLiterals(work);
  work = stripped.core;

  // The core must consist only of digit placeholders, optional thousands
  // commas, and an optional single decimal point.
  if (!/^[#0,.\s]+$/.test(work)) return null;
  if ((work.match(/\./g) || []).length > 1) return null;

  const dotIdx = work.lastIndexOf('.');
  const intPart = dotIdx >= 0 ? work.slice(0, dotIdx) : work;
  const decPart = dotIdx >= 0 ? work.slice(dotIdx + 1) : '';

  // Integer side must be only [#0,]; decimal side must be only [#0].
  if (intPart.length > 0 && !/^[#0,]+$/.test(intPart)) return null;
  if (decPart.length > 0 && !/^[#0]+$/.test(decPart)) return null;
  // Need at least one digit placeholder somewhere.
  if (intPart.length === 0 && decPart.length === 0) return null;

  const useGrouping = intPart.includes(',');
  const minDecimals = (decPart.match(/0/g) || []).length;
  const maxDecimals = decPart.length;

  return {
    multiplier,
    prefix: stripped.prefix,
    suffix: stripped.suffix + extraSuffix,
    useGrouping,
    minDecimals,
    maxDecimals,
  };
}

function fmtNumberCustom(value: number, format: string): string | null {
  const parsed = parseNumberFormat(format);
  if (!parsed) return null;

  const scaled = value * parsed.multiplier;
  if (!Number.isFinite(scaled)) return null;

  const body = scaled.toLocaleString('he-IL', {
    useGrouping: parsed.useGrouping,
    minimumFractionDigits: parsed.minDecimals,
    maximumFractionDigits: parsed.maxDecimals,
  });

  return parsed.prefix + body + parsed.suffix;
}

// ---------- Date parser ----------

const DATE_TOKEN_RE = /yyyy|yy|MM|M|dd|d|HH|H|hh|h|mm|m|ss|s/g;

function fmtDateCustom(value: Date, format: string): string | null {
  // Detect: must contain at least one date/time token.
  if (!DATE_TOKEN_RE.test(format)) return null;
  // .test() advances lastIndex — reset before .replace().
  DATE_TOKEN_RE.lastIndex = 0;

  const y4 = pad4(value.getFullYear());
  const y2 = pad2(value.getFullYear() % 100);
  const M2 = pad2(value.getMonth() + 1);
  const M1 = String(value.getMonth() + 1);
  const d2 = pad2(value.getDate());
  const d1 = String(value.getDate());
  const H2 = pad2(value.getHours());
  const H1 = String(value.getHours());
  const h12 = value.getHours() % 12 || 12;
  const h2 = pad2(h12);
  const h1 = String(h12);
  const m2 = pad2(value.getMinutes());
  const m1 = String(value.getMinutes());
  const s2 = pad2(value.getSeconds());
  const s1 = String(value.getSeconds());

  return format.replace(DATE_TOKEN_RE, (token) => {
    switch (token) {
      case 'yyyy': return y4;
      case 'yy':   return y2;
      case 'MM':   return M2;
      case 'M':    return M1;
      case 'dd':   return d2;
      case 'd':    return d1;
      case 'HH':   return H2;
      case 'H':    return H1;
      case 'hh':   return h2;
      case 'h':    return h1;
      case 'mm':   return m2;
      case 'm':    return m1;
      case 'ss':   return s2;
      case 's':    return s1;
      default:     return token;
    }
  });
}

// ---------- Entry point ----------

const DATE_TOKEN_DETECT = /yyyy|yy|MM\b|dd|HH|hh|ss/;

/** Apply an Excel-style format to a raw cell value. Null/undefined → ''.
 *  If the format string cannot be parsed, returns the raw value as a string
 *  (graceful degradation — never throws). */
export function applyFormat(value: unknown, format: string | null | undefined): string {
  if (value === null || value === undefined || value === '') return '';
  if (!format) return String(value);

  // Heuristic: if the format contains date tokens, try date first.
  if (DATE_TOKEN_DETECT.test(format)) {
    const d = value instanceof Date ? value : new Date(String(value));
    if (!Number.isNaN(d.getTime())) {
      const result = fmtDateCustom(d, format);
      if (result !== null) return result;
    }
  }

  // Otherwise try number.
  const n = typeof value === 'number' ? value : Number(value);
  if (Number.isFinite(n)) {
    const result = fmtNumberCustom(n, format);
    if (result !== null) return result;
  }

  // Last attempt for dates (in case the format has no obvious tokens but does
  // contain something like 'y' or 'm' alone).
  const d2 = value instanceof Date ? value : new Date(String(value));
  if (!Number.isNaN(d2.getTime())) {
    const result = fmtDateCustom(d2, format);
    if (result !== null) return result;
  }

  // Graceful fallback per spec — raw value.
  return String(value);
}
